import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import classnames from 'classnames'

import BrandLogo from 'components/ui/brandLogo'
import Icon from 'components/ui/icon'
import { UserRole } from 'features/user/types'
import {
  LOGGED_USER_CHANGED_EVENT,
  getLoggedUser,
  getUserProfile,
  isAuthenticated,
  setLoggedUser,
} from 'features/user/auth'
import * as userApi from 'features/user/api.client'
import { hasActiveBi } from 'features/doctors/types'

import { getNavItems } from './navItems'
import SidebarNav from './sidebarNav'
import ProfileMenu from './profileMenu'
import sidebarCss from './sidebar.module.scss'

/**
 * Main navigation for logged-in users: fixed sidebar on desktop,
 * top bar + off-canvas sidebar on small screens.
 */
const Sidebar: FunctionComponent<{ logout: () => void }> = ({ logout }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const role = getUserProfile()
  const isDoctor = role === UserRole.DOCTOR

  const [user, setUser] = useState(getLoggedUser())
  const [showBiMenu, setShowBiMenu] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    // Doctors always reload the user to know whether BI is enabled.
    if (!isDoctor && (getLoggedUser()?.email || !isAuthenticated())) {
      return
    }

    userApi
      .get()
      .then((loadedUser) => {
        setLoggedUser(loadedUser)
        setUser(loadedUser)
        setShowBiMenu(isDoctor && hasActiveBi(loadedUser))
      })
      .catch((error) => {
        console.error('Não foi possível carregar o usuário.', error)
        setShowBiMenu(false)
      })
  }, [isDoctor])

  useEffect(() => setMobileOpen(false), [pathname])

  useEffect(() => {
    const refreshUser = () => setUser(getLoggedUser())
    window.addEventListener(LOGGED_USER_CHANGED_EVENT, refreshUser)

    return () => window.removeEventListener(LOGGED_USER_CHANGED_EVENT, refreshUser)
  }, [])

  const navItems = useMemo(
    () => getNavItems(role, showBiMenu),
    [role, showBiMenu]
  )

  const brand = (
    <Link to="/" className={sidebarCss.brandLink} aria-label="Clic-repasse – início">
      <BrandLogo />
    </Link>
  )

  return (
    <>
      <header className={sidebarCss.mobileBar}>
        {brand}
        <button
          type="button"
          className="btn btn-ghost btn-icon"
          aria-label="Abrir menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(true)}
        >
          <Icon name="menu" size={20} />
        </button>
      </header>

      {mobileOpen && (
        <div
          className={sidebarCss.backdrop}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={classnames(sidebarCss.sidebar, mobileOpen && sidebarCss.open)}
      >
        <div className={sidebarCss.brand}>{brand}</div>
        <p className={`eyebrow ${sidebarCss.menuLabel}`}>Menu</p>
        <SidebarNav items={navItems} onNavigate={() => setMobileOpen(false)} />

        <div className={sidebarCss.footer}>
          <ProfileMenu
            name={user?.name ?? null}
            email={user?.email ?? null}
            onOpenAccount={() => navigate('/account')}
            onLogout={logout}
          />
        </div>
      </aside>
    </>
  )
}

export default Sidebar
